import { useEffect, useState } from "react";
import { Loader2, Activity, AlertTriangle, Package, Layers, Server, Link2 } from "lucide-react";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const Card = ({ title, children, testid }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4" data-testid={testid}>
    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200">{children.icon}{title}</h3>
    {children.body}
  </div>
);

const Metric = ({ label, value, warn, testid }) => (
  <div className="flex items-center justify-between border-b border-slate-800/60 py-1.5 last:border-0" data-testid={testid}>
    <span className="text-sm text-slate-400">{label}</span>
    <span className={`text-sm font-semibold tabular-nums ${warn ? "text-amber-400" : "text-slate-100"}`}>{value}</span>
  </div>
);

export default function AdminCatalogueHealth() {
  const { t } = useContent();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/catalogue/health").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>;
  if (!data) return <div className="py-16 text-center text-slate-400" data-testid="health-error">{t("admin.health.error", "Impossible de charger la santé du catalogue.")}</div>;

  const { totals, orphans, candidates, categories, providers, provider_services, mapping_inconsistencies } = data;

  return (
    <div className="space-y-5" data-testid="catalogue-health">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><Activity className="h-6 w-6 text-accent" /> {t("admin.health.title", "Santé du catalogue")}</h1>
        <p className="mt-1 text-sm text-slate-400" data-testid="health-readonly-note">{t("admin.health.subtitle", "Diagnostic en lecture seule — aucune action de réparation, suppression ou publication.")}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card title={t("admin.health.totals", "Services TWP")} testid="health-totals">{{
          icon: <Package className="h-4 w-4 text-sky-400" />,
          body: <>
            <Metric label={t("admin.health.total", "Total")} value={totals.total} testid="health-total" />
            <Metric label={t("admin.svc.filter.published", "Publiés (active)")} value={totals.active} testid="health-active" />
            <Metric label="Private" value={totals.private} testid="health-private" />
            <Metric label="Provider unavailable" value={totals.provider_unavailable} warn={totals.provider_unavailable > 0} testid="health-prov-unavail" />
            <Metric label={t("admin.svc.filter.archived", "Archivés")} value={totals.archived} testid="health-archived" />
            <Metric label={t("admin.health.non_archived", "Non archivés")} value={totals.non_archived} testid="health-non-archived" />
          </>,
        }}</Card>

        <Card title={t("admin.health.orphans", "Références orphelines")} testid="health-orphans">{{
          icon: <AlertTriangle className="h-4 w-4 text-amber-400" />,
          body: <>
            <Metric label={t("admin.health.missing_provider", "provider_id manquant")} value={orphans.missing_provider_id} warn={orphans.missing_provider_id > 0} testid="health-missing-provider" />
            <Metric label={t("admin.health.missing_primary", "primary_provider manquant")} value={orphans.missing_primary_provider} warn={orphans.missing_primary_provider > 0} testid="health-missing-primary" />
            <Metric label={t("admin.health.historical_orphan", "Orphelins HISTORIQUES (archivés/inactifs)")} value={orphans.historical_orphans} testid="health-historical-orphans" />
            <Metric label={t("admin.health.operational_orphan", "Orphelins OPÉRATIONNELS (actifs)")} value={orphans.operational_orphans} warn={orphans.operational_orphans > 0} testid="health-operational-orphans" />
          </>,
        }}</Card>

        <Card title={t("admin.health.candidates", "Candidats fournisseurs")} testid="health-candidates">{{
          icon: <Link2 className="h-4 w-4 text-violet-400" />,
          body: <>
            <Metric label={t("admin.health.zero_cand", "Services sans candidat approuvé")} value={candidates.zero_candidate_total} testid="health-zero-cand" />
            <Metric label={t("admin.health.active_zero", "ACTIFS sans candidat (non commandables)")} value={candidates.active_zero_candidate} warn={candidates.active_zero_candidate > 0} testid="health-active-zero" />
            <Metric label={t("admin.health.private_zero", "Privés sans candidat")} value={candidates.private_zero_candidate} testid="health-private-zero" />
          </>,
        }}</Card>

        <Card title={t("admin.health.categories", "Catégories")} testid="health-categories">{{
          icon: <Layers className="h-4 w-4 text-emerald-400" />,
          body: <>
            <Metric label={t("admin.health.cat_total", "Total")} value={categories.total} testid="health-cat-total" />
            <Metric label={t("admin.health.cat_zero", "Sans service")} value={categories.zero_service} warn={categories.zero_service > 0} testid="health-cat-zero" />
            <Metric label={t("admin.health.cat_orphan", "Réfs catégorie orphelines")} value={categories.orphan_refs} warn={categories.orphan_refs > 0} testid="health-cat-orphan" />
          </>,
        }}</Card>

        <Card title={t("admin.health.providers", "Fournisseurs")} testid="health-providers">{{
          icon: <Server className="h-4 w-4 text-amber-400" />,
          body: <>
            <Metric label={t("admin.health.prov_zero", "Sans provider_services")} value={providers.zero_provider_services} warn={providers.zero_provider_services > 0} testid="health-prov-zero" />
            <Metric label={t("admin.health.ps_unmapped", "provider_services non mappés")} value={provider_services.unmapped} testid="health-ps-unmapped" />
            <div className="mt-2 space-y-1">
              {(providers.detail || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs text-slate-400" data-testid={`health-provider-${p.id}`}>
                  <span className="truncate">{p.name} <span className="opacity-60">({p.status})</span></span>
                  <span className="tabular-nums text-slate-200">{p.provider_services}</span>
                </div>
              ))}
            </div>
          </>,
        }}</Card>

        <Card title={t("admin.health.mapping", "Cohérence du mapping")} testid="health-mapping">{{
          icon: <AlertTriangle className="h-4 w-4 text-rose-400" />,
          body: <Metric label={t("admin.health.pid_ne_primary", "provider_id ≠ primary_provider_id")} value={mapping_inconsistencies.provider_id_ne_primary} warn={mapping_inconsistencies.provider_id_ne_primary > 0} testid="health-pid-ne-primary" />,
        }}</Card>
      </div>

      {(orphans.operational_orphan_list || []).length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="health-operational-list">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-300"><AlertTriangle className="h-4 w-4" /> {t("admin.health.op_list", "Services ACTIFS orphelins (à examiner — aucune action automatique)")}</h3>
          <ul className="space-y-1 text-sm text-slate-300">
            {orphans.operational_orphan_list.map((s) => (
              <li key={s.id} className="flex items-center gap-2" data-testid={`health-op-orphan-${s.id}`}>
                <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-300">ACTIF · SANS CANDIDAT</span>
                <span className="truncate">{s.name}</span>
                <span className="text-xs text-slate-500">{s.id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
